import { AxiosError } from "axios";
import { getAllPaginatedItems } from "utilities/get-all-paginated-items";
import { IAgent, IAgentGetQueryParameters, Route } from "@edgetier/watchtower-types";
import { useQuery, UseQueryOptions } from "react-query";

// Default ordering unless specified.
export const DEFAULT_ORDER_BY = "name.asc";

/**
 * Get all agents and sort them alphabetically.
 * @param url        Agents URL.
 * @param parameters Query parameters to filter agents.
 * @returns          Agents sorted by name.
 */
export const getAgents = async (url: string, parameters: IAgentGetQueryParameters) => {
    const defaultParameters = { orderBy: DEFAULT_ORDER_BY };
    return getAllPaginatedItems<IAgent>(url, { params: { ...defaultParameters, ...parameters } });
};

/**
 * Get all agents. By default these are cached for a long time as they will very rarely change.
 * @returns     React Query result.
 */
const useAgents = <T = IAgent[]>(
    parameters: IAgentGetQueryParameters = {},
    options: Omit<UseQueryOptions<IAgent[], AxiosError, T>, "queryKey" | "queryFn"> = {}
) => {
    const url = Route.Agents;
    const hasParameters = Object.keys(parameters).length > 0;
    return useQuery<IAgent[], AxiosError, T>([url, parameters], () => getAgents(url, parameters), {
        cacheTime: hasParameters ? undefined : Infinity,
        staleTime: hasParameters ? undefined : Infinity,
        ...options,
    });
};

export default useAgents;
